import { computed, onScopeDispose, ref } from "vue";
import { defineStore } from "pinia";
import { authApi } from "@/api/gameglee";
import { ApiError, clearTokens, getAccessToken, getRefreshToken, getSessionId, getSessionUser, updateSessionUser, onTokensChanged, saveTokens, assertSession, sessionChanged, advanceAuthRevision, getAuthRevision } from "@/api/client";
import { getDeviceId, resetDeviceId } from "@/api/starparks";
import type { AccountDeletionReceipt, AuthResponse, User } from "@/api/types";
import { readStorage, writeStorage, removeStorage, requireCredentialStorage } from "@/utils/storage";

const GUEST_CREDENTIAL_KEY = "gameglee.web.guestCredential";
const GUEST_OWNER_KEY = "gameglee.web.guestOwner";
interface GuestOwner { userId: string; credential: string }
function guestOwner(): GuestOwner | null {
  try { return JSON.parse(readStorage(GUEST_OWNER_KEY) ?? "null") as GuestOwner | null; } catch { return null; }
}
function guestCredential(): string {
  let value = readStorage(GUEST_CREDENTIAL_KEY);
  if (value) return value;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  writeStorage(GUEST_CREDENTIAL_KEY, value);
  return value;
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(getSessionUser());
  const loading = ref(false);
  const ready = ref(false);
  const hasSession = ref(Boolean(getAccessToken() || getRefreshToken()));
  const sessionId = ref(getSessionId());
  const hasGuestCredential = ref(Boolean(readStorage(GUEST_CREDENTIAL_KEY)));
  let disposeTokenListener: (() => void) | undefined;
  let loginSequence = 0;

  onScopeDispose(() => disposeTokenListener?.());

  const isAuthenticated = computed(() => Boolean(user.value && hasSession.value));
  const isPremium = computed(() => (user.value?.membershipTier ?? 0) > 0);

  function accept(result: AuthResponse): void {
    saveTokens(result.tokens, result.user);
    user.value = result.user;
    sessionId.value = getSessionId();
    hasSession.value = true;
  }

  function syncSession(): void {
    sessionId.value = getSessionId();
    user.value = getSessionUser();
    hasSession.value = Boolean(getAccessToken() || getRefreshToken());
    hasGuestCredential.value = Boolean(readStorage(GUEST_CREDENTIAL_KEY));
  }

  async function bootstrap(): Promise<void> {
    if (ready.value) return;
    loading.value = true;
    disposeTokenListener ??= onTokensChanged(syncSession);
    const initialSessionId = sessionId.value;
    try {
      if (hasSession.value) updateSessionUser(await authApi.me(), initialSessionId);
    } catch (cause) {
      if (cause instanceof ApiError && (cause.status === 401 || cause.status === 403)) {
        clearTokens(initialSessionId);
        syncSession();
      }
    } finally {
      loading.value = false;
      ready.value = true;
    }
  }

  async function login(operation: () => Promise<AuthResponse>, signal?: AbortSignal, beforeAccept?: (result: AuthResponse) => void): Promise<User> {
    const expectedSession = sessionId.value;
    assertSession(expectedSession);
    if (signal?.aborted) throw sessionChanged();
    requireCredentialStorage();
    const revision = advanceAuthRevision();
    const sequence = ++loginSequence;
    loading.value = true;
    const abort = () => { if (sequence === loginSequence) loading.value = false; };
    signal?.addEventListener("abort", abort, { once: true });
    try {
      const result = await operation();
      if (signal?.aborted || sequence !== loginSequence || getAuthRevision() !== revision) throw sessionChanged();
      assertSession(expectedSession);
      beforeAccept?.(result);
      accept(result);
      return result.user;
    } finally {
      signal?.removeEventListener("abort", abort);
      if (sequence === loginSequence) loading.value = false;
      ready.value = true;
    }
  }

  async function continueAsGuest(signal?: AbortSignal): Promise<User> {
    let credential = "";
    return login(() => {
      credential = guestCredential(); hasGuestCredential.value = true;
      return authApi.guest(credential, getDeviceId());
    }, signal, (result) => {
      // A legacy credential gets an owner only after authenticating that credential.
      if (readStorage(GUEST_CREDENTIAL_KEY) === credential) writeStorage(GUEST_OWNER_KEY, JSON.stringify({ credential, userId: result.user.userId }));
    });
  }

  async function loginWithProvider(provider: string, credential: string, nonce?: string, signal?: AbortSignal): Promise<User> {
    return login(() => authApi.provider(provider, credential, getDeviceId(), nonce), signal);
  }

  async function refreshUser(): Promise<User | null> {
    if (!hasSession.value) return null;
    const expected = sessionId.value;
    const result = await authApi.me();
    updateSessionUser(result, expected);
    return result;
  }

  async function rename(nickname: string): Promise<void> {
    const expected = sessionId.value;
    updateSessionUser(await authApi.updateNickname(nickname.trim()), expected);
  }

  async function bindProvider(provider: string, credential: string, nonce?: string, expected = sessionId.value): Promise<User> {
    assertSession(expected);
    const result = await authApi.bindIdentity(provider, credential, nonce);
    updateSessionUser(result, expected);
    return result;
  }

  async function logout(): Promise<boolean> {
    // Bind to the identity currently displayed, even before a storage event arrives.
    const expected = sessionId.value;
    try { assertSession(expected); } catch { syncSession(); return false; }
    ++loginSequence; loading.value = false;
    advanceAuthRevision();
    try { await authApi.logout(); } catch (cause) {
      if (cause instanceof ApiError && cause.code === "SESSION_CHANGED") { syncSession(); return false; }
      // Network failure still permits local logout of this same session.
    }
    if (getSessionId() !== expected) { syncSession(); return false; }
    clearTokens(expected);
    syncSession();
    return true;
  }

  async function resetGuestIdentity(): Promise<void> {
    const expected = sessionId.value;
    const credential = readStorage(GUEST_CREDENTIAL_KEY);
    const device = readStorage("gameglee.web.device");
    const matchesDisplayedIdentity = getSessionId() === expected;
    if (!await logout() || !matchesDisplayedIdentity) return;
    // A different tab can sign in while logout is pending; its recovery data is not ours.
    if (getSessionId() || readStorage(GUEST_CREDENTIAL_KEY) !== credential || readStorage("gameglee.web.device") !== device) return;
    removeStorage(GUEST_CREDENTIAL_KEY);
    removeStorage(GUEST_OWNER_KEY);
    resetDeviceId();
    syncSession();
  }

  async function deleteAccount(): Promise<AccountDeletionReceipt> {
    const expected = sessionId.value;
    const deletedUserId = user.value?.userId;
    const credential = readStorage(GUEST_CREDENTIAL_KEY);
    const owner = guestOwner();
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const receiptToken = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    const receipt = await authApi.deleteAccount(receiptToken);
    if (getSessionId() === expected) {
      clearTokens(expected);
      // Unknown legacy ownership is preserved until the credential is authenticated.
      if (owner?.userId === deletedUserId && owner?.credential === credential && readStorage(GUEST_CREDENTIAL_KEY) === credential) {
        removeStorage(GUEST_CREDENTIAL_KEY);
        removeStorage(GUEST_OWNER_KEY);
        resetDeviceId();
      }
    }
    syncSession();
    return receipt;
  }

  return {
    user, sessionId, loading, ready, hasSession, hasGuestCredential, isAuthenticated, isPremium,
    bootstrap, continueAsGuest, loginWithProvider, refreshUser, rename, bindProvider,
    logout, resetGuestIdentity, deleteAccount,
  };
});
